/* global gtag */
import { isNil } from "ramda";

import { jjLogger } from "utils/logUtils";
import OfferPresenter from "presenters/OfferPresenter";
import UserPresenter from "presenters/UserPresenter";
import SubscriptionPresenter from "presenters/SubscriptionPresenter";
import TemplatePresenter from "presenters/TemplatePresenter";
import CategoryPresenter from "presenters/CategoryPresenter";
import HomePagePresenter from "presenters/HomePagePresenter";
import TemplateGroupPresenter from "presenters/TemplateGroupPresenter";
import MakePresenter from "presenters/MakePresenter";
import PersonPresenter from "presenters/PersonPresenter";
import { appRoutes } from "routes";
import { shareMethods } from "enums/shareMethods";

const TRACK_LOGIN_AND_SIGNUP_METHODS = ["email", "facebook"];
const TRACK_SHARE_METHODS = Object.values(shareMethods);

export const editSubscriptionActions = Object.freeze({
  KEEP_RENEWAL: "keep renewal",
  CANCEL_RENEWAL: "cancel renewal",
  ENABLE_RENEWAL: "enable renewal",
});

const TRACK_EDIT_SUBSCRIPTION_ACTIONS = Object.values(editSubscriptionActions);

const trackEvent = (name, params) => {
  gtag("event", name, params);
};

const trackLoginOrSignUpSuccess = (event, method) => {
  if (!TRACK_LOGIN_AND_SIGNUP_METHODS.includes(method)) {
    jjLogger.logError(`trackLoginOrSignUpSuccess: Invalid method: ${method}`);
    return;
  }

  trackEvent(event, { method });
};

export const trackLoginSuccess = (method) => {
  trackLoginOrSignUpSuccess("login", method);
};

export const trackSignUpSuccess = (method) => {
  trackLoginOrSignUpSuccess("sign_up", method);
};

export const trackPurchaseSuccess = (offer, currentUser, template) => {
  const subscription = UserPresenter.activeSubscription(currentUser);
  if (isNil(subscription) || isNil(offer)) {
    jjLogger.logError(
      `trackPurchaseSuccess: Required params not defined. Subscription: ${JSON.stringify(
        subscription,
      )} Offer: ${JSON.stringify(offer)} `,
    );
    return;
  }

  let inFlow = false;
  let item = { item_id: "out-of-flow" };

  if (!isNil(template)) {
    inFlow = true;
    item = {
      item_id: TemplatePresenter.templateGroupSlug(template),
      item_name: TemplatePresenter.templateGroupName(template),
      item_variant: TemplatePresenter.castNum(template),
    };
  }
  const params = {
    currency: OfferPresenter.currency(offer),
    transaction_id: SubscriptionPresenter.id(subscription),
    value: OfferPresenter.price(offer),
    affiliation: SubscriptionPresenter.paymentProviderName(subscription),
    coupon: OfferPresenter.id(offer),
    items: [item],
    inFlow,
  };

  trackEvent("purchase", params);
};

export const trackSearch = (searchTerm) => {
  trackEvent("search", {
    search_term: searchTerm,
  });
};

export const trackShare = (method, templateGroup) => {
  if (!TRACK_SHARE_METHODS.includes(method)) {
    jjLogger.logError(`trackShare: Invalid method: ${method}`);
    return;
  }

  const type = TemplateGroupPresenter.templateGroupType(templateGroup);
  const id = TemplateGroupPresenter.id(templateGroup);

  if (isNil(type) || isNil(id)) {
    jjLogger.logError(
      `trackShare: required params can't be empty. content_type: ${type}. item_id: ${id} templateGroup: ${JSON.stringify(
        templateGroup,
      )}`,
    );
    return;
  }

  trackEvent("share", {
    method,
    content_type: type,
    item_id: id,
  });
};

export const trackSelectContent = (templateGroup) => {
  const type = TemplateGroupPresenter.templateGroupType(templateGroup);
  const id = TemplateGroupPresenter.id(templateGroup);

  if (isNil(type) || isNil(id)) {
    jjLogger.logError(
      `trackSelectContent: required params can't be empty. content_type: ${type}. item_id: ${id} templateGroup: ${JSON.stringify(
        templateGroup,
      )}`,
    );
    return;
  }

  trackEvent("select_content", {
    content_type: type,
    item_id: id,
  });
};

export const trackMakeCreationSuccess = (make) => {
  const template = MakePresenter.template(make);
  const id = TemplatePresenter.templateGroupSlug(template);
  const status = MakePresenter.status(make);

  if (isNil(id) || isNil(status)) {
    jjLogger.logError(
      `trackMakeCreationSuccess: item_id or make status information can't be empty. template: ${JSON.stringify(
        template,
      )} make: ${JSON.stringify(make)}`,
    );
    return;
  }

  trackEvent("make_creation_success", {
    item_id: id,
    make_status: status,
  });
};

export const trackMakeCreationError = (error) => {
  if (isNil(error)) {
    jjLogger.logError(`trackMakeCreationError: error message can't be empty. Error: ${error}`);
    return;
  }

  trackEvent("make_creation_error", {
    make_creation_error_value: error,
  });
};

export const trackMakeViewSuccess = (rendererType) => {
  if (isNil(rendererType)) {
    jjLogger.logError(`trackMakeViewSuccess: renderer type message can't be empty.`);
    return;
  }

  trackEvent("make_view_success", {
    renderer: rendererType,
  });
};

export const trackMakeViewError = (rendererType) => {
  if (isNil(rendererType)) {
    jjLogger.logError(`trackMakeViewError: renderer type can't be empty.`);
    return;
  }

  trackEvent("make_view_error", {
    renderer: rendererType,
  });
};

export const trackTemplateView = (template) => {
  if (isNil(template)) {
    jjLogger.logError(`trackTemplateView: required params can't be empty. template: ${JSON.stringify(template)}`);
    return;
  }

  trackEvent("view_item", {
    items: [
      {
        item_id: TemplatePresenter.templateGroupSlug(template),
        item_name: TemplatePresenter.templateGroupName(template),
        item_variant: TemplatePresenter.castNum(template),
      },
    ],
  });
};

export const trackSelectItem = (itemListInfo, item) => {
  if (isNil(itemListInfo) || isNil(item)) {
    jjLogger.logError(
      `trackSelectItem: required params can't be empty. itemListInfo: ${JSON.stringify(
        itemListInfo,
      )} item: ${JSON.stringify(item)} `,
    );
    return;
  }

  const { slug, name } = item;
  const { itemListId, itemListName } = itemListInfo;

  trackEvent("select_item", {
    item_list_id: itemListId,
    item_list_name: itemListName,
    items: [
      {
        item_id: slug,
        item_name: name,
      },
    ],
  });
};

export const handlerSelectItemEvent = (item, category, homePage = null) => {
  let itemListInfo;
  const categoryPath = appRoutes.categoryPath(CategoryPresenter.id(category));
  const homePath = appRoutes.rootPath();
  const searchPath = appRoutes.searchPath();
  const { pathname, search } = window.location;
  const queryParams = new URLSearchParams(search);

  switch (true) {
    case category && categoryPath === pathname:
      itemListInfo = {
        itemListId: CategoryPresenter.id(category),
        itemListName: CategoryPresenter.displayName(category),
      };
      break;
    case homePage && homePath === pathname:
      itemListInfo = {
        itemListId: HomePagePresenter.id(homePage),
        itemListName: HomePagePresenter.name(homePage),
      };
      break;
    case searchPath === pathname:
      if (queryParams.has("term")) {
        const term = `term=${queryParams.get("term")}`;
        itemListInfo = {
          itemListId: term,
          itemListName: term,
        };
      }
      break;
    default:
      break;
  }

  trackSelectItem(itemListInfo, item);
};

export const track404 = (url) => {
  if (isNil(url)) {
    jjLogger.logError(`track404: url can't be empty. url: ${url}`);
    return;
  }

  trackEvent("404", {
    404: url,
  });
};

export const trackEditSubscriptionBilling = () => {
  trackEvent("edit_subscription_billing", {
    billing_info: "billing info updated",
  });
};

export const trackEditSubscriptionSuccess = (action) => {
  if (isNil(action) || !TRACK_EDIT_SUBSCRIPTION_ACTIONS.includes(action)) {
    jjLogger.logError(`trackEditSubscriptionSuccess: Invalid keep renewal action: ${action}`);
    return;
  }

  trackEvent("edit_subscription_success", {
    keep_renewal: action,
  });
};

export const trackSubscriptionFeedback = (answer) => {
  if (isNil(answer)) {
    jjLogger.logError(`trackSubscriptionFeedback: Invalid feedback: ${answer}`);
    return;
  }

  trackEvent("feedback", {
    feedback: answer,
  });
};

export const trackCalendarHit = (occasion) => {
  if (isNil(occasion)) {
    jjLogger.logError(`trackCalendarHit: Invalid occasion: occasion should not be nil or empty`);
    return;
  }

  trackEvent("calendar_hit", {
    occasion,
  });
};

export const trackCalendarDiscovery = (action) => {
  if (isNil(action)) {
    jjLogger.logError("trackCalendarDiscovery: Invalid action: action should not be nil or empty");
    return;
  }

  trackEvent("calendar_discovery", {
    calendar_discovery: action,
  });
};

const trackPersonSuccessEvent = (functionName, eventType, person) => {
  if (isNil(person)) {
    jjLogger.logError(`${functionName}: Required params not defined. Person: ${JSON.stringify(person)}`);
    return;
  }

  const params = {};
  if (PersonPresenter.relation(person)) params.relation = PersonPresenter.relation(person);
  if (PersonPresenter.birthday(person)) params.birthday = "yes";
  if (PersonPresenter.anniversary(person)) params.anniversary = "yes";

  trackEvent(eventType, params);
};

export const trackPersonCreationSuccess = (person) => {
  trackPersonSuccessEvent("trackPersonCreationSuccess", "person_created", person);
};

export const trackPersonUpdateSuccess = (person) => {
  trackPersonSuccessEvent("trackPersonUpdateSuccess", "person_updated", person);
};
